import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/format";
import type { ListResponse, WebhookDelivery, WebhookEndpoint } from "../../lib/types";
import {
  Button,
  CopyButton,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  ModePill,
  PageHeader,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

interface EndpointsResponse {
  data: WebhookEndpoint[] | null;
  available_events: string[];
  signature_header: string;
  signature_scheme: string;
}

export function Webhooks() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<"sandbox" | "live">("sandbox");
  const [description, setDescription] = useState("");
  const [created, setCreated] = useState<{ secret: string; url: string } | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);

  const endpoints = useQuery({
    queryKey: ["webhooks"],
    queryFn: () => api.get<EndpointsResponse>("/api/dashboard/webhooks"),
  });

  const deliveries = useQuery({
    queryKey: ["webhook-deliveries"],
    queryFn: () => api.get<ListResponse<WebhookDelivery>>("/api/dashboard/webhooks/deliveries?limit=25"),
    refetchInterval: 20_000,
  });

  const create = useMutation({
    mutationFn: () => api.post<{ secret: string; url: string }>("/api/dashboard/webhooks", { url, mode, description }),
    onSuccess: (result) => {
      setCreated(result);
      setCreating(false);
      setUrl("");
      setDescription("");
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/dashboard/webhooks/${id}`),
    onSuccess: () => {
      setDeleting(null);
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/api/dashboard/webhooks/deliveries/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] }),
  });

  const rows = endpoints.data?.data ?? [];
  const deliveryRows = deliveries.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Integration"
        title="Webhooks"
        description="We notify your server when a payment reaches a final state. Every delivery is signed so you can prove it came from us."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add endpoint
          </Button>
        }
      />

      <Sheet className="settle settle-1 mb-6 overflow-hidden">
        <SheetHeader title="Endpoints" />
        {endpoints.isLoading ? (
          <Skeleton rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No endpoints yet"
            description="Add a URL and we will POST every payment outcome to it."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add your first endpoint
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-rule">
            {rows.map((endpoint) => (
              <li key={endpoint.id} className="flex items-start justify-between gap-4 px-5 py-3.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="truncate font-mono text-[12.5px] text-ink">{endpoint.url}</code>
                    <ModePill mode={endpoint.mode} />
                    {endpoint.disabled && <StatusPill status="failed" />}
                  </div>
                  <p className="mt-1 text-[11.5px] text-ink-faint">
                    {endpoint.events && endpoint.events.length > 0
                      ? `${endpoint.events.length} event types`
                      : "All events"}
                    {endpoint.consecutive_failures > 0 &&
                      ` · ${endpoint.consecutive_failures} consecutive failures`}
                    {endpoint.description && ` · ${endpoint.description}`}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(endpoint)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Sheet>

      <Sheet className="settle settle-2 mb-6 overflow-hidden">
        <SheetHeader
          title="Recent deliveries"
          description="What we sent, and how your server answered"
        />
        {deliveries.isLoading ? (
          <Skeleton rows={5} />
        ) : deliveryRows.length === 0 ? (
          <EmptyState title="No deliveries yet" description="Events appear here once payments start completing." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Event</Th>
                <Th>Status</Th>
                <Th align="right">Attempts</Th>
                <Th align="right">Response</Th>
                <Th align="right">When</Th>
                <Th align="right" />
              </tr>
            </thead>
            <tbody>
              {deliveryRows.map((delivery) => (
                <Tr key={delivery.id}>
                  <Td>
                    <span className="font-mono text-[12px]">{delivery.event_type}</span>
                    {delivery.last_error && (
                      <span className="mt-0.5 block max-w-[280px] truncate text-[11px] text-sienna">
                        {delivery.last_error}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <StatusPill status={delivery.status} />
                  </Td>
                  <Td align="right" className="tabular">
                    {delivery.attempts}
                  </Td>
                  <Td align="right" mono>
                    {delivery.response_status ?? "—"}
                  </Td>
                  <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                    {formatRelative(delivery.created_at)}
                  </Td>
                  <Td align="right">
                    {delivery.status !== "delivered" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={retry.isPending && retry.variables === delivery.id}
                        onClick={() => retry.mutate(delivery.id)}
                      >
                        Retry
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Sheet>

      <Sheet className="settle settle-3 overflow-hidden">
        <SheetHeader title="Verifying a delivery" description="Check the signature before you trust the body" />
        <div className="px-5 py-4">
          <p className="mb-3 text-[13px] leading-relaxed text-ink-muted">
            Each request carries{" "}
            <code className="font-mono text-[12px] text-ink">
              {endpoints.data?.signature_header ?? "X-PSP-Signature"}
            </code>
            . Recompute the HMAC over the timestamp and the raw body, compare it in constant
            time, and reject anything older than five minutes.
          </p>
          <pre className="overflow-x-auto rounded-md bg-paper-sunk px-4 py-3 font-mono text-[11.5px] leading-relaxed text-ink-muted">
{`// Node — verify before parsing the body
const [t, v1] = header.split(",").map(p => p.split("=")[1]);

if (Math.abs(Date.now() / 1000 - Number(t)) > 300) reject();

const expected = crypto
  .createHmac("sha256", process.env.WEBHOOK_SECRET)
  .update(\`\${t}.\${rawBody}\`)
  .digest("hex");

if (!crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(expected))) reject();

// Answer 2xx quickly, then do the work asynchronously.
res.sendStatus(200);`}
          </pre>
          <p className="mt-3 text-[12px] leading-relaxed text-ink-muted">
            Deliveries are retried with backoff for about 24 hours. Treat them as
            at-least-once: key your handler on the event id so a repeat is harmless.
          </p>
        </div>
      </Sheet>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Add a webhook endpoint"
        description="We will POST a signed JSON body to this URL."
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Add endpoint
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorNotice error={create.error} />
          <Field
            label="Endpoint URL"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-server.co.zw/webhooks/bushveld"
            required
          />
          <SelectField
            label="Mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as "sandbox" | "live")}
            options={[
              { value: "sandbox", label: "Sandbox" },
              { value: "live", label: "Live" },
            ]}
            hint="A live endpoint must use https."
          />
          <Field
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Order fulfilment service"
          />
        </div>
      </Modal>

      <Modal
        open={Boolean(created)}
        onClose={() => setCreated(null)}
        title="Signing secret"
        description="Copy it now — it is shown only once."
        footer={
          <Button variant="primary" onClick={() => setCreated(null)}>
            Stored safely
          </Button>
        }
      >
        {created && (
          <div className="space-y-3">
            <p className="text-[13px] text-ink-muted">
              Use this secret to verify deliveries to{" "}
              <code className="font-mono text-[12px] text-ink">{created.url}</code>.
            </p>
            <div className="flex items-center justify-between gap-2 rounded-md border border-verdigris/30 bg-verdigris-wash px-3 py-2">
              <code className="truncate font-mono text-[12px] text-verdigris">{created.secret}</code>
              <CopyButton value={created.secret} />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Remove this endpoint?"
        description="We will stop sending events to it. Deliveries already queued will not be retried."
        footer={
          <>
            <Button onClick={() => setDeleting(null)}>Keep it</Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Remove endpoint
            </Button>
          </>
        }
      >
        {deleting && (
          <>
            <ErrorNotice error={remove.error} className="mb-3" />
            <code className="font-mono text-[12.5px]">{deleting.url}</code>
          </>
        )}
      </Modal>
    </>
  );
}
